import { useState, useEffect, useCallback } from 'react';
import type { Promotion } from '@/lib/types';
import {
  getPromotions,
  savePromotion,
  deletePromotion,
} from '@/lib/api';
import ImageUploader from '@/pages/admin/components/ImageUploader';

const emptyForm: Omit<Promotion, 'id' | 'created_at' | 'updated_at'> = {
  title: '',
  subtitle: '',
  description: '',
  badge: '',
  discount: '',
  image: '',
  cta: 'Ver Producto',
  product_id: null,
  sort_order: 0,
};

export default function AdminPromociones() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [form, setForm] = useState<Omit<Promotion, 'id' | 'created_at' | 'updated_at'>>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchPromotions = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getPromotions();
      setPromotions(data);
    } catch (error) {
      console.error(error);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const openCreate = () => {
    setEditingPromo(null);
    setForm({ ...emptyForm, sort_order: promotions.length + 1 });
    setModalOpen(true);
  };

  const openEdit = (promo: Promotion) => {
    setEditingPromo(promo);
    setForm({
      title: promo.title,
      subtitle: promo.subtitle,
      description: promo.description,
      badge: promo.badge,
      discount: promo.discount,
      image: promo.image,
      cta: promo.cta,
      product_id: promo.product_id,
      sort_order: promo.sort_order,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;

    setSaving(true);

    try {
      if (editingPromo) {
        await savePromotion({
          ...form,
          id: editingPromo.id,
        });

        showToast("Promoción actualizada correctamente");
      } else {
        await savePromotion(form);

        showToast("Promoción creada correctamente");
      }

      setModalOpen(false);

      await fetchPromotions();
    } catch (error) {
      console.error(error);
      showToast("Error al guardar la promoción");
    }

    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePromotion(id);

      showToast("Promoción eliminada");

      await fetchPromotions();
    } catch (error) {
      console.error(error);
    }

    setDeleteConfirm(null);
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#2d1a0e] text-white text-sm px-5 py-3 rounded-xl">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-emerald-400"></i>
            {toast}
          </span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#2d1a0e]">
            Gestión de Promociones
          </h1>
          <p className="text-sm text-[#8a6a55]">
            {promotions.length} promociones en el slider principal
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#00b4c8] hover:bg-[#0099aa] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-sm"></i>
          </span>
          Nueva Promoción
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <span className="w-5 h-5 flex items-center justify-center text-amber-600 flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-base"></i>
        </span>
        <p className="text-sm text-amber-800">
          Las promociones aparecen en el slider de la página de inicio. El orden de aparición corresponde al campo de orden.
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#8a6a55] text-sm">
          <i className="ri-loader-4-line text-2xl animate-spin text-[#00b4c8] block mx-auto mb-2"></i>
          Cargando promociones...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {promotions.map((promo, index) => (
            <div key={promo.id} className="bg-white rounded-xl border border-[#f0e6d8] overflow-hidden">
              <div className="relative h-40">
                <img
                  src={promo.image}
                  alt={promo.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="bg-[#d4607a] text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    #{index + 1}
                  </span>
                  {promo.badge && (
                    <span className="bg-white/90 text-[#2d1a0e] text-xs px-2 py-0.5 rounded-full font-medium">
                      {promo.badge}
                    </span>
                  )}
                </div>
                {promo.discount && (
                  <span className="absolute top-3 right-3 bg-amber-400 text-[#2d1a0e] text-xs px-2 py-0.5 rounded-full font-bold">
                    {promo.discount}
                  </span>
                )}
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-bold text-sm leading-tight">{promo.title}</p>
                  <p className="text-white/80 text-xs">{promo.subtitle}</p>
                </div>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-[#8a6a55] line-clamp-2 mb-3">{promo.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-[#f8f4ff] text-[#7c5cbf] px-2 py-1 rounded-lg border border-[#c9b8f0]/30">
                    CTA: {promo.cta}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(promo)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] text-[#6b5b6e] hover:text-[#00b4c8] transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(promo.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#5a3e2b] hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={openCreate}
            className="bg-white rounded-xl border-2 border-dashed border-[#c9b8f0]/40 hover:border-[#00b4c8] h-[220px] flex flex-col items-center justify-center gap-2 text-[#6b5b6e] hover:text-[#00b4c8] transition-colors cursor-pointer"
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#fdf6f0]">
              <i className="ri-add-line text-xl"></i>
            </span>
            <span className="text-sm font-medium">Agregar Promoción</span>
          </button>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-[#c9b8f0]/20 flex items-center justify-between">
              <h3 className="font-bold text-[#2d1a0e]">
                {editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-[#6b5b6e] text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Título *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                  placeholder="Ej: Pastel de Tres Leches"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Subtítulo
                </label>
                <input
                  type="text"
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                  placeholder="Ej: El clásico mexicano que enamora"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Descripción
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors resize-none"
                  placeholder="Descripción breve de la promoción..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Badge
                  </label>
                  <input
                    type="text"
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                    placeholder="Ej: NUEVO"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Descuento / Oferta
                  </label>
                  <input
                    type="text"
                    value={form.discount}
                    onChange={(e) => setForm({ ...form, discount: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                    placeholder="Ej: 20% OFF"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Texto del Botón (CTA)
                </label>
                <input
                  type="text"
                  value={form.cta}
                  onChange={(e) => setForm({ ...form, cta: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                  placeholder="Ej: Ver Producto"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Imagen de la Promoción
                </label>
                <ImageUploader
                  value={form.image}
                  onChange={(base64) => setForm({ ...form, image: base64 })}
                  accentColor="#00b4c8"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#c9b8f0]/20 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#6b5b6e] hover:bg-[#f8f4ff] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || saving}
                className="px-4 py-2 rounded-lg bg-[#00b4c8] hover:bg-[#0099aa] text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                {editingPromo ? 'Guardar Cambios' : 'Crear Promoción'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-center font-bold text-[#2d1a0e] mb-2">¿Eliminar promoción?</h3>
            <p className="text-center text-sm text-[#8a6a55] mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-[#e8d5c4] text-sm text-[#5a3e2b] hover:bg-[#fdf6f0] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
